/**
 * TypeScript entities for BL-002 Admin Billing Reports Feature
 * Based on backend models: BillingReportFilterModel, BillingReportModel, BillingOrderSummaryModel
 */

// Filter interface matching BillingReportFilterModel.cs
export interface IBillingReportFilter {
    statusFilter?: number | null; // 1=NotBilled, 2=All, 3=BilledOnly, 4=InBatch, 5=NotInBatch
    orderStatusFilter?: number | null; // 0=All, 1=Draft, 2=Sent, 3=Billed (matches OrderStatusEnum)
    startDate?: Date | null;
    endDate?: Date | null;
    healthcareFacilityIDs?: number[] | null;
    minAmount?: number | null;
    maxAmount?: number | null;
    pageNumber: number; // 1-based pagination
    pageSize: number;
    sortBy: string; // default: "CreatedDate"
    sortDirection: string; // "ASC" or "DESC"
}

// Order summary interface matching BillingOrderSummaryModel.cs
export interface IBillingOrderSummary {
    orderID: number;
    orderReference: string;
    orderDate: Date;
    healthcareFacilityName: string;
    contactName: string;
    contactEmail: string;
    totalAmount: number;
    orderStatusID: number;
    orderStatusName: string;
    billedDateTime?: Date | null;
    billedByUserName?: string | null;
    // Step 45: Billing batch information
    billingBatchID?: number | null;
    billingBatchNumber?: string | null;
}

// Report response interface matching BillingReportModel.cs
export interface IBillingReport {
    orders: IBillingOrderSummary[];
    totalCount: number;
    totalUnbilledAmount: number;
    totalBilledAmount: number;
    unbilledOrderCount: number;
    billedOrderCount: number;
    reportGeneratedAt: Date;
}

// Request model for marking orders as billed (MarkBilledRequestModel.cs)
export interface IMarkBilledRequest {
    orderIDs: number[];
    billedByUserID: number;
}

// Response model for batch mark-as-billed operations (MarkBilledResponseModel.cs)
export interface IMarkBilledResponse {
    totalOrders: number;
    successfullyBilled: number;
    failed: number;
    successfulOrderIDs?: number[];
    errors?: IOrderBillingError[];
    success: boolean;
    message: string;
}

// Error details for individual order billing failures
export interface IOrderBillingError {
    orderID: number;
    errorMessage: string;
    errorType: string;
}

// Classes implementing the interfaces
export class BillingReportFilter implements IBillingReportFilter {
    statusFilter?: number | null;
    orderStatusFilter?: number | null;
    startDate?: Date | null;
    endDate?: Date | null;
    healthcareFacilityIDs?: number[] | null;
    minAmount?: number | null;
    maxAmount?: number | null;
    pageNumber: number = 1;
    pageSize: number = 25;
    sortBy: string = 'CreatedDate';
    sortDirection: string = 'DESC';

    constructor(data?: Partial<IBillingReportFilter>) {
        this.statusFilter = data?.statusFilter ?? null;
        this.orderStatusFilter = data?.orderStatusFilter ?? null;
        this.startDate = data?.startDate ?? null;
        this.endDate = data?.endDate ?? null;
        this.healthcareFacilityIDs = data?.healthcareFacilityIDs ?? null;
        this.minAmount = data?.minAmount ?? null;
        this.maxAmount = data?.maxAmount ?? null;
        this.pageNumber = data?.pageNumber ?? 1;
        this.pageSize = data?.pageSize ?? 25;
        this.sortBy = data?.sortBy ?? 'CreatedDate';
        this.sortDirection = data?.sortDirection ?? 'DESC';
    }

    // Helper method to convert to API-compatible object
    toApiObject(): Record<string, any> {
        return {
            StatusFilter: this.statusFilter,
            OrderStatusFilter: this.orderStatusFilter,
            StartDate: this.startDate?.toISOString(),
            EndDate: this.endDate?.toISOString(),
            HealthcareFacilityIDs: this.healthcareFacilityIDs,
            MinAmount: this.minAmount,
            MaxAmount: this.maxAmount,
            PageNumber: this.pageNumber,
            PageSize: this.pageSize,
            SortBy: this.sortBy,
            SortDirection: this.sortDirection,
        };
    }
}

export class BillingOrderSummary implements IBillingOrderSummary {
    orderID: number;
    orderReference: string;
    orderDate: Date;
    healthcareFacilityName: string;
    contactName: string;
    contactEmail: string;
    totalAmount: number;
    orderStatusID: number;
    orderStatusName: string;
    billedDateTime?: Date | null;
    billedByUserName?: string | null;
    // Step 45: Billing batch information
    billingBatchID?: number | null;
    billingBatchNumber?: string | null;

    constructor(data: IBillingOrderSummary) {
        this.orderID = data.orderID;
        this.orderReference = data.orderReference;
        this.orderDate = new Date(data.orderDate);
        this.healthcareFacilityName = data.healthcareFacilityName;
        this.contactName = data.contactName;
        this.contactEmail = data.contactEmail;
        this.totalAmount = data.totalAmount;
        this.orderStatusID = data.orderStatusID;
        this.orderStatusName = data.orderStatusName;
        this.billedDateTime = data.billedDateTime ? new Date(data.billedDateTime) : null;
        this.billedByUserName = data.billedByUserName;
        this.billingBatchID = data.billingBatchID;
        this.billingBatchNumber = data.billingBatchNumber;
    }

    // Helper property to check if order is billed
    get isBilled(): boolean {
        return !!this.billedDateTime;
    }
    
    // Step 45: Helper property to check if order is in a billing batch
    get isInBatch(): boolean {
        return !!this.billingBatchID;
    }

    // Helper property for display formatting
    get formattedTotalAmount(): string {
        return `$${this.totalAmount.toFixed(2)}`;
    }

    get formattedOrderDate(): string {
        return this.orderDate.toLocaleDateString();
    }

    get formattedBilledDateTime(): string | null {
        return this.billedDateTime?.toLocaleDateString() ?? null;
    }
}

export class BillingReport implements IBillingReport {
    orders: BillingOrderSummary[];
    totalCount: number;
    totalUnbilledAmount: number;
    totalBilledAmount: number;
    unbilledOrderCount: number;
    billedOrderCount: number;
    reportGeneratedAt: Date;

    constructor(data: IBillingReport) {
        this.orders = (data.orders || []).map(order => new BillingOrderSummary(order));
        this.totalCount = data.totalCount || 0;
        this.totalUnbilledAmount = data.totalUnbilledAmount || 0;
        this.totalBilledAmount = data.totalBilledAmount || 0;
        this.unbilledOrderCount = data.unbilledOrderCount || 0;
        this.billedOrderCount = data.billedOrderCount || 0;
        this.reportGeneratedAt = new Date(data.reportGeneratedAt);
    }

    // Helper properties for display formatting
    get formattedTotalUnbilledAmount(): string {
        return `$${this.totalUnbilledAmount.toFixed(2)}`;
    }

    get formattedTotalBilledAmount(): string {
        return `$${this.totalBilledAmount.toFixed(2)}`;
    }

    get formattedReportGeneratedAt(): string {
        return this.reportGeneratedAt.toLocaleString();
    }
}

export class MarkBilledRequest implements IMarkBilledRequest {
    orderIDs: number[];
    billedByUserID: number;

    constructor(data: IMarkBilledRequest) {
        this.orderIDs = data.orderIDs;
        this.billedByUserID = data.billedByUserID;
    }
}

export class MarkBilledResponse implements IMarkBilledResponse {
    totalOrders: number;
    successfullyBilled: number;
    failed: number;
    successfulOrderIDs: number[];
    errors: OrderBillingError[];
    success: boolean;
    message: string;

    constructor(data: IMarkBilledResponse) {
        this.totalOrders = data.totalOrders;
        this.successfullyBilled = data.successfullyBilled;
        this.failed = data.failed;
        this.successfulOrderIDs = data.successfulOrderIDs || [];
        this.errors = (data.errors || []).map(error => new OrderBillingError(error));
        this.success = data.success;
        this.message = data.message;
    }

    // Helper properties for display
    get hasErrors(): boolean {
        return this.errors.length > 0;
    }

    get partialSuccess(): boolean {
        return this.successfullyBilled > 0 && this.failed > 0;
    }
}

export class OrderBillingError implements IOrderBillingError {
    orderID: number;
    errorMessage: string;
    errorType: string;

    constructor(data: IOrderBillingError) {
        this.orderID = data.orderID;
        this.errorMessage = data.errorMessage;
        this.errorType = data.errorType;
    }
}

// ================================
// BILLING BATCH TYPES - Step 41
// ================================

// Filter interface matching BillingBatchFilterModel.cs
export interface IBillingBatchFilter {
    startDate?: Date | null;
    endDate?: Date | null;
    createdByUserID?: number | null;
    minAmount?: number | null;
    maxAmount?: number | null;
    pageNumber: number; // 1-based pagination
    pageSize: number;
    sortBy: string; // default: "CreatedDateTime"
    sortDirection: string; // "ASC" or "DESC"
}

// Batch summary interface matching BillingBatchSummaryModel.cs
export interface IBillingBatchSummary {
    id: number;
    batchNumber: string;
    createdDateTime: Date;
    createdByUserName: string;
    orderCount: number;
    totalAmount: number;
    csvFileName: string;
}

// Complete batch interface matching BillingBatchModel.cs
export interface IBillingBatch {
    id: number;
    batchNumber: string;
    createdDateTime: Date;
    createdByUserID: number;
    createdByUserName: string;
    orderCount: number;
    totalAmount: number;
    csvFileName: string;
    orders: IBillingOrderSummary[];
}

// Batch list response interface matching BillingBatchListModel.cs
export interface IBillingBatchList {
    batches: IBillingBatchSummary[];
    totalCount: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalBatches: number;
    totalOrders: number;
    totalAmount: number;
}

// Request model for creating billing batches
export interface ICreateBillingBatchRequest {
    orderIDs: number[];
    description?: string;
}

// Classes implementing the billing batch interfaces
export class BillingBatchFilter implements IBillingBatchFilter {
    startDate?: Date | null;
    endDate?: Date | null;
    createdByUserID?: number | null;
    minAmount?: number | null;
    maxAmount?: number | null;
    pageNumber: number = 1;
    pageSize: number = 25;
    sortBy: string = 'CreatedDateTime';
    sortDirection: string = 'DESC';

    constructor(data?: Partial<IBillingBatchFilter>) {
        this.startDate = data?.startDate ?? null;
        this.endDate = data?.endDate ?? null;
        this.createdByUserID = data?.createdByUserID ?? null;
        this.minAmount = data?.minAmount ?? null;
        this.maxAmount = data?.maxAmount ?? null;
        this.pageNumber = data?.pageNumber ?? 1;
        this.pageSize = data?.pageSize ?? 25;
        this.sortBy = data?.sortBy ?? 'CreatedDateTime';
        this.sortDirection = data?.sortDirection ?? 'DESC';
    }

    // Helper method to convert to API-compatible object
    toApiObject(): Record<string, any> {
        return {
            StartDate: this.startDate?.toISOString(),
            EndDate: this.endDate?.toISOString(),
            CreatedByUserID: this.createdByUserID,
            MinAmount: this.minAmount,
            MaxAmount: this.maxAmount,
            PageNumber: this.pageNumber,
            PageSize: this.pageSize,
            SortBy: this.sortBy,
            SortDirection: this.sortDirection,
        };
    }
}

export class BillingBatchSummary implements IBillingBatchSummary {
    id: number;
    batchNumber: string;
    createdDateTime: Date;
    createdByUserName: string;
    orderCount: number;
    totalAmount: number;
    csvFileName: string;

    constructor(data: IBillingBatchSummary) {
        this.id = data.id;
        this.batchNumber = data.batchNumber;
        this.createdDateTime = new Date(data.createdDateTime);
        this.createdByUserName = data.createdByUserName;
        this.orderCount = data.orderCount;
        this.totalAmount = data.totalAmount;
        this.csvFileName = data.csvFileName;
    }

    // Helper properties for display formatting
    get formattedTotalAmount(): string {
        return `$${this.totalAmount.toFixed(2)}`;
    }

    get formattedCreatedDateTime(): string {
        return this.createdDateTime.toLocaleDateString();
    }

    get formattedCreatedDateTimeWithTime(): string {
        return this.createdDateTime.toLocaleString();
    }
}

export class BillingBatch implements IBillingBatch {
    id: number;
    batchNumber: string;
    createdDateTime: Date;
    createdByUserID: number;
    createdByUserName: string;
    orderCount: number;
    totalAmount: number;
    csvFileName: string;
    orders: BillingOrderSummary[];

    constructor(data: IBillingBatch) {
        this.id = data.id;
        this.batchNumber = data.batchNumber;
        this.createdDateTime = new Date(data.createdDateTime);
        this.createdByUserID = data.createdByUserID;
        this.createdByUserName = data.createdByUserName;
        this.orderCount = data.orderCount;
        this.totalAmount = data.totalAmount;
        this.csvFileName = data.csvFileName;
        this.orders = (data.orders || []).map(order => new BillingOrderSummary(order));
    }

    // Helper properties for display formatting
    get formattedTotalAmount(): string {
        return `$${this.totalAmount.toFixed(2)}`;
    }

    get formattedCreatedDateTime(): string {
        return this.createdDateTime.toLocaleDateString();
    }

    get formattedCreatedDateTimeWithTime(): string {
        return this.createdDateTime.toLocaleString();
    }
}

export class BillingBatchList implements IBillingBatchList {
    batches: BillingBatchSummary[];
    totalCount: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalBatches: number;
    totalOrders: number;
    totalAmount: number;

    constructor(data: IBillingBatchList) {
        this.batches = (data.batches || []).map(batch => new BillingBatchSummary(batch));
        this.totalCount = data.totalCount || 0;
        this.currentPage = data.currentPage || 1;
        this.pageSize = data.pageSize || 25;
        this.totalPages = data.totalPages || 0;
        this.totalBatches = data.totalBatches || 0;
        this.totalOrders = data.totalOrders || 0;
        this.totalAmount = data.totalAmount || 0;
    }

    // Helper properties for display formatting
    get formattedTotalAmount(): string {
        return `$${this.totalAmount.toFixed(2)}`;
    }

    get hasResults(): boolean {
        return this.batches.length > 0;
    }

    get hasPreviousPage(): boolean {
        return this.currentPage > 1;
    }

    get hasNextPage(): boolean {
        return this.currentPage < this.totalPages;
    }
}

export class CreateBillingBatchRequest implements ICreateBillingBatchRequest {
    orderIDs: number[];
    description?: string;

    constructor(data: ICreateBillingBatchRequest) {
        this.orderIDs = data.orderIDs;
        this.description = data.description;
    }

    // Helper method to convert to API-compatible object
    toApiObject(): Record<string, any> {
        return {
            OrderIDs: this.orderIDs,
            Description: this.description,
        };
    }
} 